import { Locale } from '@kamernet/core/Intl/types';

import { defineRoute } from './defineRoute';
import { AboutUs } from './routes/AboutUs';
import { Cashback } from './routes/Cashback';
import { CookiePolicy } from './routes/CookiePolicy';
import { EmailUnsubscribe } from './routes/EmailUnsubscribe';
import { GlobalListingSearch } from './routes/GlobalListingSearch';
import { GlobalTenantSearch } from './routes/GlobalTenantSearch';
import { Home } from './routes/Home';
import { HowDoesItWorkLandlord } from './routes/HowDoesItWorkLandlord';
import { HowDoesItWorkTenant } from './routes/HowDoesItWorkTenant';
import { LandlordPremium } from './routes/LandlordPremium';
import { LandlordPublicProfile } from './routes/LandlordPublicProfile';
import { LCFAddress } from './routes/LCFAddress';
import { LCFAvailability } from './routes/LCFAvailability';
import { LCFContinue } from './routes/LCFContinue';
import { LCFCurrentResidents } from './routes/LCFCurrentResidents';
import { LCFDetails } from './routes/LCFDetails';
import { LCFListingType } from './routes/LCFListingType';
import { LCFMedia } from './routes/LCFMedia';
import { LCFOwnerType } from './routes/LCFOwnerType';
import { LCFPreferredTenant } from './routes/LCFPreferredTenant';
import { LCFReactivate } from './routes/LCFReactivate';
import { LCFRentalPrice } from './routes/LCFRentalPrice';
import { LCFStart } from './routes/LCFStart';
import { LCFStudentHouse } from './routes/LCFStudentHouse';
import { LCFSupplyProducts } from './routes/LCFSupplyProducts';
import { LCFSurfaceFurnishing } from './routes/LCFSurfaceFurnishing';
import { LCFTitleDescription } from './routes/LCFTitleDescription';
import { LegacyLogin } from './routes/LegacyLogin';
import { ListingsSearchSitemapByCityID } from './routes/ListingsSearchSitemapByCityID';
import { ListingsSearchSitemapByListingTypeID } from './routes/ListingsSearchSitemapByListingTypeID';
import { MakePayment } from './routes/MakePayment';
import { MyAccount } from './routes/MyAccount';
import { MyAlerts } from './routes/MyAlerts';
import { MyConversation } from './routes/MyConversation';
import { MyConversations } from './routes/MyConversations';
import { MyDashboard } from './routes/MyDashboard';
import { MyFavoriteListings } from './routes/MyFavoriteListings';
import { MyFavoriteTenants } from './routes/MyFavoriteTenants';
import { MyListings } from './routes/MyListings';
import { MyPublicProfile } from './routes/MyPublicProfile';
import { MySettings } from './routes/MySettings';
import { Partnerships } from './routes/Partnerships';
import { PaymentReminder } from './routes/PaymentReminder';
import { PaymentStatus } from './routes/PaymentStatus';
import { PremiumListing } from './routes/PremiumListing';
import { PrivacyPolicy } from './routes/PrivacyPolicy';
import { ReactToListing } from './routes/ReactToListing';
import { ReactToListingSuccess } from './routes/ReactToListingSuccess';
import { ReferFriend } from './routes/ReferFriend';
import { RentOutHome } from './routes/RentOutHome';
import { RentOutRoom } from './routes/RentOutRoom';
import { SafetyLandlord } from './routes/SafetyLandlord';
import { SafetyTenant } from './routes/SafetyTenant';
import { Signin } from './routes/Signin';
import { Signout } from './routes/Signout';
import { Signup } from './routes/Signup';
import { StudentHouse } from './routes/StudentHouse';
import { TenantPublicProfile } from './routes/TenantPublicProfile';
import { TermsAndConditions } from './routes/TermsAndConditions';
import { UnsubscribeEmail } from './routes/UnsubscribeEmail';
import {
  ListingDetailsRouteParams,
  ListingSearchCityRouteParams,
  ListingSearchStreetRouteParams,
} from './types';

export * from './types';

export const ROUTES = Object.freeze({
  Home,
  Signin,
  Signup,
  Signout,
  LegacyLogin,
  GlobalListingSearch,
  GlobalTenantSearch,
  MyDashboard,
  MyConversations,
  ReactToListing,
  ReactToListingSuccess,
  MyConversation,
  MyAccount,
  MyPublicProfile,
  MyAlerts,
  MySettings,
  ReferFriend,
  Cashback,
  MyFavoriteListings,
  MyFavoriteTenants,
  LCFStart,
  LCFContinue,
  LCFListingType,
  LCFOwnerType,
  LCFStudentHouse,
  LCFAddress,
  LCFAvailability,
  LCFSurfaceFurnishing,
  LCFRentalPrice,
  LCFMedia,
  LCFDetails,
  LCFPreferredTenant,
  LCFCurrentResidents,
  LCFTitleDescription,
  LCFSupplyProducts,
  LCFReactivate,
  MyListings,
  RentOutRoom,
  RentOutHome,
  StudentHouse,
  SafetyLandlord,
  SafetyTenant,
  HowDoesItWorkTenant,
  HowDoesItWorkLandlord,
  TermsAndConditions,
  PrivacyPolicy,
  CookiePolicy,
  PremiumListing,
  MakePayment,
  PaymentReminder,
  AboutUs,
  PaymentStatus,
  Partnerships,
  LandlordPremium,
  UnsubscribeEmail,

  ListingSearchCity: defineRoute<ListingSearchCityRouteParams>({
    pathname: '/for-rent/[typeAndCity]',
    nlPathname: '/huren/[typeAndCity]',
  }),

  ListingSearchStreet: defineRoute<ListingSearchStreetRouteParams>({
    pathname: '/for-rent/[typeAndCity]/[street]',
    nlPathname: '/huren/[typeAndCity]/[street]',
  }),

  ListingDetails: defineRoute<ListingDetailsRouteParams>({
    pathname: '/for-rent/[typeAndCity]/[street]/[typeAndListingId]',
    nlPathname: '/huren/[typeAndCity]/[street]/[typeAndListingId]',
  }),

  LandlordPublicProfile,

  PreviewRoomAdvert: defineRoute<{ listingId: string; from: string }>({
    pathname: '/preview-roomadvert/[listingId]/[from]',
    nlPathname: '/voorbeeldweergave/[listingId]/[from]',
    isLandlordRoute: true,
  }),

  EmailUnsubscribe,

  ListingsSearchSitemapByListingTypeID,
  ListingsSearchSitemapByCityID,
  TenantPublicProfile,

  // @placeholder:route <= used by kamernet-page code generator to inject route definition
});

/** routes that are publicly accessible but changes visually when a user is authenticated */
export const PUBLIC_ROUTES: readonly string[] = Object.freeze([
  ROUTES.Home.pathname,
  ROUTES.GlobalListingSearch.pathname,
  ROUTES.RentOutRoom.pathname,
  ROUTES.HowDoesItWorkTenant.pathname,
  ROUTES.HowDoesItWorkLandlord.pathname,
  ROUTES.TermsAndConditions.pathname,
  ROUTES.PrivacyPolicy.pathname,
  ROUTES.SafetyLandlord.pathname,
  ROUTES.SafetyTenant.pathname,
  ROUTES.AboutUs.pathname,
  ROUTES.CookiePolicy.pathname,
  ROUTES.ListingSearchCity.pathname,
  ROUTES.ListingSearchStreet.pathname,
  ROUTES.ListingDetails.pathname,
  ROUTES.PaymentStatus.pathname,
  ROUTES.PaymentReminder.pathname,
  ROUTES.LandlordPublicProfile.pathname,
  ROUTES.EmailUnsubscribe.pathname,
  ROUTES.ListingsSearchSitemapByListingTypeID.pathname,
  ROUTES.ListingsSearchSitemapByCityID.pathname,
  ROUTES.TenantPublicProfile.pathname,
  ROUTES.PremiumListing.pathname,
  // ...
]);

export const ROUTE_TO_IMPLEMENTATION_MAPPING = Object.freeze(
  Object.fromEntries(
    Object.values(ROUTES).map(v => [v.pathnameByLocale[Locale.nl], v.pathname]),
  ),
) as Record<string, string>;

export function getRouteByPathname(pathname: string) {
  return (
    Object.values(ROUTES).find(route => route.pathname === pathname) || null
  );
}
